import React from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { extractTemplateExamples, extractTemplateParameterName, formatTemplateVariableLabel } from '../../utils/templateVariableLabels';

interface SampleCsvSectionProps {
  template_name?: string;
  templateVariables: any[];
  components?: any[];
}

const SampleCsvSection: React.FC<SampleCsvSectionProps> = ({ template_name, templateVariables, components = [] }) => {
  const getExampleData = () => {

    // Extract examples from template components
    const templateExamples = extractTemplateExamples(components)

    const examples: Record<string, string> = {
      phone: '911234567890',
      full_name: 'Rohan',
      ...templateExamples
    };

    return examples;
  };

  const templateColumnDetails = templateVariables.map((variable) => {
    const parameterName = extractTemplateParameterName(
      (variable as any).parameter_name || variable.placeholder,
      variable.index
    );

    return {
      key: variable.index,
      variable: variable.placeholder,
      columnName: formatTemplateVariableLabel(parameterName, variable.index),
      parameterName,
    };
  });

  const handleDownload = () => {
    const exampleData = getExampleData();
    const headers = ['Contact', 'Full Name (Optional)', ...templateColumnDetails.map((d) => d.columnName)];

    const row = [
      exampleData.phone,
      exampleData.full_name,
      ...templateColumnDetails.map((d) => exampleData[d.parameterName] || ''),
    ];

    const csvContent = [
      headers.join(','),
      row.join(','),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_audience.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exampleData = getExampleData();

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'start', mb: 1 }}>
        <Typography variant="h6">Sample CSV for {template_name ? <Box component='span'>template: <Box component='span' sx={{fontStyle:'italic'}}> {template_name}</Box></Box> : "your selected template"}</Typography>
      </Box>
      <TableContainer component={Paper} sx={{ border: 0.1, borderColor:'text.secondary', boxShadow:'none' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Full Name (Optional)</TableCell>
              {templateColumnDetails.map((detail) => (
                <TableCell key={detail.key} sx={{ fontWeight: 600 }}>
                  {detail.columnName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{exampleData.phone}</TableCell>
              <TableCell>{exampleData.full_name}</TableCell>
              {templateColumnDetails.map((detail) => (
                <TableCell key={detail.key}>
                  {exampleData[detail.parameterName] || 'Example Value'}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        sx={{ mt: 1 }}
        size="medium"
        startIcon={<DownloadIcon />}
        onClick={handleDownload}
      >
        Download Sample CSV
      </Button>
    </Box>
  );
};

export default SampleCsvSection;
